'use client';

/* ──────────────────────────────────────────────
 *  DynamicFormFields — Renders custom fields
 *  configured by the professional
 * ────────────────────────────────────────────── */

import type { BookingCustomField, CustomFieldAnswer } from '../../types';
import { FormField } from './FormField';

interface DynamicFormFieldsProps {
  /**
   * Champs DÉJÀ filtrés pour la réservation en cours (`useCustomFieldAnswers().visibleFields`) :
   * ce composant n'évalue aucune règle de visibilité — l'affichage ne peut donc pas diverger de la
   * validation « requis » ni des réponses envoyées.
   */
  fields: BookingCustomField[];
  answers: CustomFieldAnswer[];
  onUpdateAnswer: (fieldId: number, value: string | number | boolean) => void;
  errors: Record<number, string>;
  onBlurField: (fieldId: number) => void;
  sectionLabel: string;
}

export function DynamicFormFields({
  fields,
  answers,
  onUpdateAnswer,
  errors,
  onBlurField,
  sectionLabel,
}: DynamicFormFieldsProps) {
  if (fields.length === 0) return null;

  return (
    <div className="bt-checkout__section">
      <h3 className="bt-checkout__section-title">{sectionLabel}</h3>
      <div className="bt-checkout__fields">
        {fields.map((field): React.ReactNode => {
          const answer = answers.find((a): boolean => a.custom_field_id === field.custom_field_id);
          return (
            <FormField
              key={field.custom_field_id}
              id={`custom-field-${field.custom_field_id}`}
              label={field.label}
              type={field.type}
              value={answer?.value ?? (field.type === 'checkbox' ? false : '')}
              onChange={(val): void => onUpdateAnswer(field.custom_field_id, val)}
              required={field.required}
              placeholder={field.placeholder}
              options={field.options}
              min={field.min}
              max={field.max}
              error={errors[field.custom_field_id]}
              onBlur={(): void => onBlurField(field.custom_field_id)}
            />
          );
        })}
      </div>
    </div>
  );
}
